import { createReducer } from 'redux-starter-kit';
import { handleError } from '../../modules/utils/handleError';
import i18next from 'i18next';
import { VoucherAppointmentsApi } from './VoucherAppointmentsApi';
import * as Dict from '../../modules/utils/Dict';
import Notice from '../../modules/utils/Notice';
import { NCALayerSign } from '../../components/NCALayer/NCALayerSign';
import { VoucherApplicationsApi } from '../VoucherApplications/VoucherApplicationsApi';
import moment from 'moment';
import { history } from '../../_helpers/history';
import paths from '../../_helpers/paths';
import _ from 'lodash';

/**
 * Constants
 */

export const voucherAppointmentsModule = 'voucherAppointments';
const SET_DATA = `${voucherAppointmentsModule}/SET_DATA`;
const LOADING = `${voucherAppointmentsModule}/LOADING`;
const CLEAR_STATE = `${voucherAppointmentsModule}/CLEAR_STATE`;

/**
 * Reducer
 */

const initialState = {
  loading: true,
  data: [],
  pageNumber: 1,
  totalElements: 0
};

export default createReducer(initialState, {
  [SET_DATA]: (state, action) => {
    state.data = action.data;
    state.pageNumber = action.pageNumber;
    state.totalElements =
      action.pageNumber === 1 ? action.totalElements : state.totalElements;
  },
  [LOADING]: (state, action) => {
    state.loading = action.loading;
  },
  [CLEAR_STATE]: () => initialState
});

export const clearState = () => ({ type: CLEAR_STATE });

const getKeyValues = data => {
  let res = [];
  data.forEach(item => {
    let obj = {};
    item.passportValues.forEach(a => {
      obj[a.code] = a.value;
    });
    res.push(obj);
  });
  return res;
};

export const loadAppointments = filter => async dispatch => {
  try {
    const { data } = await VoucherAppointmentsApi.getAppointments(JSON.stringify(filter));
    for (let row of data.result.content) {
      row.providerLocation = await Dict.item('60', row.queuedRegion);
    }
    if (data.status === 'SUCCESS') {
      const content = [];
      for (let item of data.result.content) {
        let queuedType = item.queuedType
          ? await Dict.item('gu_queued_type', item.queuedType || '')
          : '';
        let appStatusWeb = item.status
          ? await Dict.item('voucher_status_web', item.status || '')
          : '';
        let priorityQueued = item.priorityQueued
          ? await Dict.item('priority_queued', item.priorityQueued || '')
          : '';
        let priorityCategory = item.priorityCategory
          ? await Dict.item('priority_category', item.priorityCategory || '')
          : '';

        content.push({
          ...item,
          queuedTypeStatus: queuedType,
          appStatusWebStatus: appStatusWeb,
          priorityQueuedStatus: priorityQueued,
          priorityCategory: priorityCategory
        });
      }
      dispatch({
        type: SET_DATA,
        data: content,
        pageNumber: data.result.pageNumber,
        totalElements: data.result.totalElements
      });
    } else {
      Notice.error(i18next.t('voucher_appointments_errorLoad'));
    }
  } catch (error) {
    handleError(error, i18next.t('voucher_appointments_errorLoad'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};
export const prolongVoucher = (id, modal, filter) => async dispatch => {
  try {
    dispatch({ type: LOADING, loading: true });

    await VoucherAppointmentsApi.prolongVoucher({
      id: id
    });
    dispatch(loadAppointments(filter));
    modal.close();
  } catch (error) {
    handleError(error, i18next.t('voucher_applications_error_prolongVoucher'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};
export const declineVoucher = (modalData, modal, filter) => async dispatch => {
  try {
    dispatch({ type: LOADING, loading: true });
    const { data } = await VoucherAppointmentsApi.getSignForDeclineVoucher({
      voucherId: modalData.id
    });
    const signature = await NCALayerSign(data.result);
    if (signature) {
      const { data } = await VoucherAppointmentsApi.declineVoucher({
        voucherId: modalData.id,
        signature: signature
      });
      if (data.status === 'SUCCESS') {
        Notice.success(i18next.t('voucher_applications_successDeclineVoucher'));
        dispatch(loadAppointments(filter));
        modal.close();
      }
    }
  } catch (error) {
    const errCode = error.response.data.errors[0]?.code;
    if (
      errCode === 'error.runtime_kindergarten_voucher_contingent_applicant_or_enrolled'
    ) {
      Notice.error(
        i18next.t('runtime_kindergarten_voucher_contingent_applicant_or_enrolled', {
          fullName: `${modalData.childSurname} ${modalData.childName}`,
          iin: `${modalData.childIin}`
        })
      );
    } else if (errCode === 'error.runtime_user_must_sing_own_signature') {
      Notice.error(i18next.t('must_sing_own_signature'));
    } else if (errCode === 'error.runtime_signature_data_was_changed') {
      Notice.error(i18next.t('runtime_signature_data_was_changed'));
    } else if (errCode === 'error.runtime_signature_is_invalid') {
      Notice.error(i18next.t('runtime_signature_is_invalid'));
    } else {
      handleError(error, i18next.t('voucher_applications_errorDeclineVoucher'));
    }
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};

export const appointmentDownload = ({ requestFormId }) => async dispatch => {
  try {
    dispatch({ type: LOADING, loading: true });
    const response = await VoucherAppointmentsApi.appointmentDownload(requestFormId);
    download(
      response.data,
      `${i18next.t('voucher_appointments_subtitleDownload')}-${requestFormId}.pdf`
    );
  } catch (error) {
    handleError(error, i18next.t('voucher_applications_errorLoad'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};

const download = (data, fileName) => {
  let element = document.createElement('a');
  document.body.appendChild(element);
  element.style.display = 'none';
  let blob = new Blob([data]);
  let url = window.URL.createObjectURL(blob);
  element.href = url;
  element.download = fileName;
  element.click();
  window.URL.revokeObjectURL(url);
};

export const canGetVoucher = (data, push) => async dispatch => {
  try {
    dispatch({ type: LOADING, loading: true });
    let filter = {
      locationCode: data.queuedRegion,
      statusWeb: data.appStatus === 'IN_QUEUED' ? 'available_all' : null,
      childBirthDay: moment(data.childBirthday).format('YYYY-MM-DD'),
      queuedType: data.queuedType,
      rehabType: data.rehabType,
      correctionType: data.correctionType
    };
    const response = await VoucherApplicationsApi.getBulletins(filter);
    if (response.data.content) {
      let res = getKeyValues(response.data.content);
      let check = false;
      for (let i = 0; i < res.length; i++) {
        if (res[i].available_sits > 0) {
          check = true;
          break;
        }
      }

      if (check) {
        push(
          `/bulletin-open-places/voucherId=${data.id}?isForReservePlace=true&filter=${encodeURIComponent(
            JSON.stringify({
              pageNumber: 1,
              pageSize: 10,
              locationCode: data.queuedRegion,
              statusWeb: data.appStatus === 'IN_QUEUED' ? 'available_all' : null,
              childBirthDay: moment(data.childBirthday).format('YYYY-MM-DD'),
              queuedType: data.queuedType,
              rehabType: data.rehabType,
              correctionType: data.correctionType
            })
          )}`
        );

      } else {
        Notice.error(i18next.t('voucher_applications_check_bulletin_empty'));
        return false;
      }
    } else {
      Notice.error(i18next.t('voucher_applications_check_bulletin_error'));
    }
  } catch (error) {
    handleError(error, i18next.t('voucher_applications_check_bulletin_error'));
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};

export const reserveDdoPlace = (id, modal) => async dispatch => {
  try {
    dispatch({ type: LOADING, loading: true });
    const { data } = await VoucherAppointmentsApi.getSignString({
      id: id,
      status: 'RECEIVED',
      bulletinId: modal.data.id
    });
    const signature = await NCALayerSign(data.result);
    if (signature) {
      await VoucherAppointmentsApi.reservePlace({
        id: id,
        status: 'NOT_ENROLLED_TO_PO',
        signature: signature,
        bulletinId: modal.data.id
      });
      modal.close();
      history.push(paths.voucherAppointments);
    }
  } catch (e) {
    const errorCode = _.get(e, 'response.data.errors[0].code');
    if (errorCode === 'error.runtime_user_must_sing_own_signature') {
      Notice.error(i18next.t('must_sing_own_signature'));
    } else {
      handleError(e, i18next.t('voucher_appointments_error_reserve_place'));
    }
  } finally {
    dispatch({ type: LOADING, loading: false });
  }
};
